import { z } from 'zod';

const packagingCategorySchema = z.enum([
  'LABEL',
  'RIBBON',
  'POUCH',
  'JAR_PLASTIC',
  'JAR_GLASS',
  'BOX',
  'BAG_THEMED',
  'KIT',
  'OIL_BOTTLE',
  'OIL_LABEL',
  'OIL_BOX',
  'TERPENE',
  'CALENDAR',
  'CONSUMABLE',
  'OTHER',
]);
export type PackagingCategory = z.infer<typeof packagingCategorySchema>;

// PackagingItem (per picker)
export const packagingItemResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: packagingCategorySchema,
  unit: z.string(),
  residualQty: z.number().int(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PackagingItemResponse = z.infer<typeof packagingItemResponseSchema>;

export const packagingItemListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(200),
  category: packagingCategorySchema.optional(),
  search: z.string().trim().min(1).max(100).optional(),
  active: z.coerce.boolean().optional(),
});
export type PackagingItemListQuery = z.infer<typeof packagingItemListQuerySchema>;

export const packagingItemListResponseSchema = z.object({
  items: z.array(packagingItemResponseSchema),
  total: z.number().int().nonnegative(),
});
export type PackagingItemListResponse = z.infer<typeof packagingItemListResponseSchema>;

// Righe INV
export const invLotLineSchema = z.object({
  lotId: z.string().uuid('Lotto richiesto'),
  position: z.string().max(100).nullable().optional(),
  qtyCounted: z.coerce.number().min(0, 'Quantita >= 0'),
  notes: z.string().max(500).nullable().optional(),
});
export type InvLotLine = z.infer<typeof invLotLineSchema>;

export const invPackagingLineSchema = z.object({
  packagingItemId: z.string().uuid('Packaging richiesto'),
  qtyCounted: z.coerce.number().int().nonnegative(),
  notes: z.string().max(500).nullable().optional(),
});
export type InvPackagingLine = z.infer<typeof invPackagingLineSchema>;

export const invFinishedSkuLineSchema = z.object({
  productId: z.string().uuid('Prodotto richiesto'),
  qty5g: z.coerce.number().int().nonnegative().default(0),
  qty10g: z.coerce.number().int().nonnegative().default(0),
  qty20g: z.coerce.number().int().nonnegative().default(0),
  qty50g: z.coerce.number().int().nonnegative().default(0),
  qty100g: z.coerce.number().int().nonnegative().default(0),
  notes: z.string().max(500).nullable().optional(),
});
export type InvFinishedSkuLine = z.infer<typeof invFinishedSkuLineSchema>;

// Create / Update / Close
export const invCreateRequestSchema = z.object({
  draftName: z.string().max(100).nullable().optional(),
  workDate: z.coerce.date(),
  notes: z.string().max(2000).nullable().optional(),
  lotLines: z.array(invLotLineSchema).default([]),
  packagingLines: z.array(invPackagingLineSchema).default([]),
  finishedLines: z.array(invFinishedSkuLineSchema).default([]),
});
export type InvCreateRequest = z.infer<typeof invCreateRequestSchema>;

export const invUpdateRequestSchema = invCreateRequestSchema.partial();
export type InvUpdateRequest = z.infer<typeof invUpdateRequestSchema>;

export const invCloseRequestSchema = z.object({}).strict();
export type InvCloseRequest = z.infer<typeof invCloseRequestSchema>;

// Response shapes hydrated
export const invLotLineResponseSchema = invLotLineSchema.extend({
  id: z.string(),
  qtyFromCdcSnapshot: z.string().nullable(),
  lotCode: z.string().nullable(),
  productCode: z.string().nullable(),
  productName: z.string().nullable(),
  qtyCounted: z.string(), // serializzato Decimal
});
export type InvLotLineResponse = z.infer<typeof invLotLineResponseSchema>;

export const invPackagingLineResponseSchema = invPackagingLineSchema.extend({
  id: z.string(),
  packagingItemCode: z.string(),
  packagingItemName: z.string(),
  packagingItemCategory: packagingCategorySchema,
});
export type InvPackagingLineResponse = z.infer<typeof invPackagingLineResponseSchema>;

export const invFinishedSkuLineResponseSchema = invFinishedSkuLineSchema.extend({
  id: z.string(),
  productCode: z.string(),
  productName: z.string(),
});
export type InvFinishedSkuLineResponse = z.infer<typeof invFinishedSkuLineResponseSchema>;

// Reconciliation
export const invReconciliationItemSchema = z.object({
  lotId: z.string(),
  lotCode: z.string(),
  productCode: z.string().nullable(),
  productName: z.string().nullable(),
  qtyCountedSum: z.string(),
  qtyFromCdc: z.string(),
  differenza: z.string(),
  notInInventory: z.boolean(),
  notInCdc: z.boolean(),
});
export type InvReconciliationItem = z.infer<typeof invReconciliationItemSchema>;

export const invReconciliationResponseSchema = z.object({
  items: z.array(invReconciliationItemSchema),
  totalDelta: z.string(),
  countWithDelta: z.number().int().nonnegative(),
  countOk: z.number().int().nonnegative(),
});
export type InvReconciliationResponse = z.infer<typeof invReconciliationResponseSchema>;

const documentStatusSchema = z.enum(['DRAFT', 'CLOSED', 'SUPERSEDED']);

export const invResponseSchema = z.object({
  id: z.string(),
  type: z.literal('INV'),
  code: z.string().nullable(),
  draftName: z.string().nullable(),
  workDate: z.string(),
  periodId: z.string(),
  period: z.object({
    year: z.number().int(),
    month: z.number().int(),
    status: z.enum(['OPEN', 'CLOSED']),
  }),
  status: documentStatusSchema,
  locked: z.boolean(),
  notes: z.string().nullable(),
  createdById: z.string(),
  closedById: z.string().nullable(),
  createdAt: z.string(),
  closedAt: z.string().nullable(),
  updatedAt: z.string(),
  lotLines: z.array(invLotLineResponseSchema),
  packagingLines: z.array(invPackagingLineResponseSchema),
  finishedLines: z.array(invFinishedSkuLineResponseSchema),
  reconciliation: invReconciliationResponseSchema,
});
export type InvResponse = z.infer<typeof invResponseSchema>;

export const invListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  status: documentStatusSchema.optional(),
  periodId: z.string().uuid().optional(),
  workDateFrom: z.coerce.date().optional(),
  workDateTo: z.coerce.date().optional(),
});
export type InvListQuery = z.infer<typeof invListQuerySchema>;

export const invListItemSchema = z.object({
  id: z.string(),
  type: z.literal('INV'),
  code: z.string().nullable(),
  draftName: z.string().nullable(),
  workDate: z.string(),
  status: documentStatusSchema,
  locked: z.boolean(),
  closedAt: z.string().nullable(),
  lotLinesCount: z.number().int().nonnegative(),
  packagingLinesCount: z.number().int().nonnegative(),
  finishedLinesCount: z.number().int().nonnegative(),
});
export type InvListItem = z.infer<typeof invListItemSchema>;

export const invListResponseSchema = z.object({
  items: z.array(invListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type InvListResponse = z.infer<typeof invListResponseSchema>;
